import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { translate, translateLabel, type Locale } from "../i18n/translate";
import { activeEventPage, EVENT_GROUPS, eventIdOf, eventPath } from "../system/eventPages";
import { activeItemFor, consoleGroupsFor, type NavItem } from "../system/navigation";

// ---------------------------------------------------------------------------
// Tour T-64 (owner, 22 October 2026; the mock-up was option A, "two rails"). Four horizontal
// rows of menus became two vertical ones: a narrow rail with the six groups, and beside it a
// panel with the active group's pages. Inside an event the panel becomes the event's own
// tree (back link, name, Overview, then Plan / Live / Money / Compliance as collapsible
// groups), so the event's sixteen pages are one click away and no row of tabs sits above the
// screen. Below 900px the same two lists open as a drawer from the header's Menu button.
// ---------------------------------------------------------------------------

/** One short glyph per group: a visual anchor for the rail, never the only signal (the word is under it). */
const GLYPH: Record<string, string> = {
  overview: "◧",
  events: "▦",
  crew: "☺",
  clients: "▣",
  "compliance-checks": "✓",
  settings: "⚙",
};

type EventHead = { name: string; status_label: string };

function useEventHead(eventId: string | null, apiBase: string): EventHead | null {
  const [head, setHead] = useState<{ id: string; head: EventHead } | null>(null);
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    void send<{ event?: { name: string; status_label: string } }>(console_.getEvent(apiBase, eventId))
      .then((res) => {
        if (!cancelled && res.status === 200 && res.body.event) setHead({ id: eventId, head: { name: res.body.event.name, status_label: res.body.event.status_label } });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId, apiBase]);
  return head && head.id === eventId ? head.head : null;
}

export function ConsoleMenu({
  items,
  pathname,
  permissions,
  locale,
  apiBase,
  open,
}: {
  items: NavItem[];
  pathname: string;
  permissions: string[];
  locale: Locale;
  apiBase: string;
  open: boolean;
}) {
  const groups = consoleGroupsFor(items);
  const active = activeItemFor(pathname, items);
  const group = groups.find((g) => active && g.items.some((i) => i.id === active.id)) ?? null;
  const eventId = eventIdOf(pathname);
  return (
    <div className={`console-menu${open ? " is-open" : ""}`}>
      <nav className="console-rail" aria-label={translate(locale, "Sections")}>
        {groups.map((g) => (
          <Link key={g.id} to={g.items[0]!.path} aria-current={g.id === group?.id ? "page" : undefined}>
            <span className="console-rail-glyph" aria-hidden="true">
              {GLYPH[g.id] ?? "•"}
            </span>
            {translateLabel(locale, g.label)}
          </Link>
        ))}
      </nav>
      {eventId || group ? (
      <aside className="console-panel" aria-label={translate(locale, "Pages")}>
        {eventId ? (
          <EventPanel eventId={eventId} pathname={pathname} permissions={permissions} locale={locale} apiBase={apiBase} />
        ) : group ? (
          <>
            <h2 className="console-panel-title">{translateLabel(locale, group.label)}</h2>
            {group.items.map((i) => (
              <NavLink key={i.id} to={i.path} end aria-current={i.id === active?.id ? "page" : undefined}>
                {translateLabel(locale, i.label)}
              </NavLink>
            ))}
          </>
        ) : null}
      </aside>
      ) : null}
    </div>
  );
}

function EventPanel({ eventId, pathname, permissions, locale, apiBase }: { eventId: string; pathname: string; permissions: string[]; locale: Locale; apiBase: string }) {
  const head = useEventHead(eventId, apiBase);
  const groups = EVENT_GROUPS.map((g) => ({ ...g, pages: g.pages.filter((p) => permissions.includes(p.requires)) })).filter((g) => g.pages.length > 0);
  const current = activeEventPage(pathname, eventId, groups);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const onOverview = pathname === `/console/events/${eventId}`;
  return (
    <>
      <Link className="console-panel-back" to="/console/events">
        ← {translate(locale, "Back to events")}
      </Link>
      <div className="console-panel-event">
        <strong>{head?.name ?? "…"}</strong>
        {head ? <span className="chip chip-neutral">{translateLabel(locale, head.status_label)}</span> : null}
      </div>
      <NavLink to={`/console/events/${eventId}`} end aria-current={onOverview ? "page" : undefined}>
        {translate(locale, "Overview")}
      </NavLink>
      {groups.map((g) => {
        const isOpen = opened[g.id] ?? current?.group.id === g.id;
        return (
          <div key={g.id} className="console-tree">
            <button type="button" aria-expanded={isOpen} onClick={() => setOpened({ ...opened, [g.id]: !isOpen })}>
              <span className="console-tree-chev" aria-hidden="true">
                ▸
              </span>
              {translateLabel(locale, g.label)}
              <span className="console-tree-count">{g.pages.length}</span>
            </button>
            {isOpen
              ? g.pages.map((p) => (
                  <NavLink key={p.screen} to={eventPath(p.screen, eventId)} end aria-current={current?.page.screen === p.screen ? "page" : undefined}>
                    {translateLabel(locale, p.label)}
                  </NavLink>
                ))
              : null}
          </div>
        );
      })}
    </>
  );
}

/** Where you are, as links: Verbunk > group > (event > its group) > page. */
export function ConsoleCrumbs({ items, pathname, permissions, locale, apiBase }: { items: NavItem[]; pathname: string; permissions: string[]; locale: Locale; apiBase: string }) {
  const groups = consoleGroupsFor(items);
  const active = activeItemFor(pathname, items);
  const group = groups.find((g) => active && g.items.some((i) => i.id === active.id)) ?? null;
  const eventId = eventIdOf(pathname);
  const head = useEventHead(eventId, apiBase);
  if (!active) return null;
  const parts: { label: string; to?: string }[] = [{ label: "Verbunk", to: "/console" }];
  if (!group) parts.push({ label: translateLabel(locale, active.label) });
  else if (group.id !== "overview") parts.push({ label: translateLabel(locale, group.label), to: group.items[0]!.path });
  if (eventId && group) {
    const visible = EVENT_GROUPS.map((g) => ({ ...g, pages: g.pages.filter((p) => permissions.includes(p.requires)) })).filter((g) => g.pages.length > 0);
    const current = activeEventPage(pathname, eventId, visible);
    parts.push({ label: head?.name ?? "…", to: `/console/events/${eventId}` });
    if (current) {
      parts.push({ label: translateLabel(locale, current.group.label) });
      parts.push({ label: translateLabel(locale, current.page.label) });
    }
  } else if (group && translateLabel(locale, active.label) !== translateLabel(locale, group.label) && group.id !== "overview") {
    parts.push({ label: translateLabel(locale, active.label) });
  }
  return (
    <nav className="console-crumbs" aria-label={translate(locale, "Breadcrumb")}>
      <ol>
        {parts.map((p, i) => (
          <li key={i} aria-current={i === parts.length - 1 ? "page" : undefined}>
            {p.to && i < parts.length - 1 ? <Link to={p.to}>{p.label}</Link> : p.label}
          </li>
        ))}
      </ol>
    </nav>
  );
}
